import z from 'zod';

import { vulnerableCountryIsoCodes } from '@sola/contentful-model/lib/sac-values.ts';
import { childSearch, getRandomChildren } from '@sola/proxy-sdk/children.ts';
import { CartItemTypeEnum } from '@sola/schema/dotcom/cart/cart-item.ts';
import { SponsorshipItemTypeEnum } from '@sola/schema/dotcom/cart/sponsorship.ts';
import type { UpsellFundItem } from '@sola/schema/dotcom/checkout/upsell.ts';
import type { UpsellSponsorshipItem } from '@sola/schema/dotcom/checkout/upsell.ts';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';

/** Parse a numeric query param, returning `undefined` for absent/invalid values. */
const numericParamSchema = z
  .string()
  .min(1)
  .transform(Number)
  .pipe(z.number())
  .optional();

/** Validate a gender param against the allowed enum values. */
const genderParamSchema = z
  .enum([ChildGenderEnum.Male, ChildGenderEnum.Female])
  .optional();

/**
 * When "Highly Vulnerable" is selected, restrict the countries list to only
 * vulnerable country ISO codes. If the user also selected specific countries,
 * intersect with those; otherwise use all vulnerable countries.
 */
const resolveCountries = (
  countries: string[] | undefined,
  isVulnerable: boolean,
): string[] | undefined => {
  if (!isVulnerable) {
    return countries;
  }

  const hvc = vulnerableCountryIsoCodes as readonly string[];

  if (countries?.length) {
    const matched = countries.filter((c) => hvc.includes(c));
    return matched.length > 0 ? matched : [...hvc];
  }

  return [...hvc];
};

/**
 * @description Get a Rich Mix child & return the structured Sponsorship item data.
 * When a `sacFilter` query string is provided, uses child search with those criteria.
 */
export const getUpsellSponsorshipItem = async (
  sacFilter?: string | null,
): Promise<UpsellSponsorshipItem | null | undefined> => {
  let children;

  if (sacFilter) {
    const params = new URLSearchParams(sacFilter);
    const filters = params.get('filters') ?? '';
    const isVulnerable = filters.includes('vulnerableExploitation');
    const rawCountries = params.get('countries')?.split(',') ?? undefined;

    let ageMin = numericParamSchema.safeParse(params.get('ageMin')).data;
    let ageMax = numericParamSchema.safeParse(params.get('ageMax')).data;

    // Swap if the caller passed an inverted range.
    if (ageMin != null && ageMax != null && ageMin > ageMax) {
      [ageMin, ageMax] = [ageMax, ageMin];
    }

    const result = await childSearch({
      ageMin,
      ageMax,
      gender: genderParamSchema.safeParse(params.get('gender')).data,
      countries: resolveCountries(rawCountries, isVulnerable),
      disability: filters.includes('hasDisabilities') ? true : undefined,
      isInHivAffectedArea: filters.includes('affectedHiv') ? true : undefined,
      isOrphan: filters.includes('orphanedChild') ? true : undefined,
      centerId: params.get('centerId'),
      take: 1,
    });
    children = result?.children;
  } else {
    const result = await getRandomChildren();
    children = result?.children;
  }

  const child = children?.[0];

  if (!child) {
    return null;
  }

  return {
    itemType: CartItemTypeEnum.Sponsorship,
    sponsorship: {
      sponsorshipType: SponsorshipItemTypeEnum.Selected,
      globalId: child.globalId,
      sourceCode: child.sourceCode || '',
    },
    ...(child && {
      child,
    }),
  };
};

/**
 * @description Build an UpsellFundItem from the campaign's upsell override fund data.
 */
export const getUpsellFundItem = (fund: {
  cmsId: string;
  icon?: string | null;
  title: string;
  unitPrice: number;
}): UpsellFundItem => ({
  itemType: CartItemTypeEnum.Fund,
  fund: {
    cmsId: fund.cmsId,
    ...(fund.icon && { icon: fund.icon }),
    title: fund.title,
  },
  unitPrice: fund.unitPrice,
});
